import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { track } from "@/lib/api";
import {
  Sparkle,
  HandsPraying,
  PlayCircle,
  ArrowRight,
  MusicNotes,
  Star,
  UsersThree,
  Heart,
  Compass,
  Wind,
  Sun,
  ShieldCheck,
  ChatCircleDots,
} from "@phosphor-icons/react";

const ICONS = { HandsPraying, Heart, Compass, ShieldCheck, Sparkle, Sun };

// Each emotional entry now routes to the personalized prayer flow with a
// shortcut prefill — the user can refine in their own words before generating.
const EMOTIONAL_ENTRIES = [
  { id: "calm",       label: "Calm my mind",        to: "/guided-prayer/new?shortcut=calm",      icon: Wind },
  { id: "direction",  label: "Find direction",      to: "/guided-prayer/new?shortcut=direction", icon: Compass },
  { id: "closer",     label: "Feel closer to God",  to: "/guided-prayer/new?shortcut=closer",    icon: Heart },
  { id: "release",    label: "Release stress",      to: "/guided-prayer/new?shortcut=release",   icon: Sun },
];

export default function HomePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [journey, setJourney] = useState(null);
  const [learn, setLearn] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
    api.get("/prayer-journeys/progress/summary").then((res) => setJourney(res.data)).catch(() => {});
    api.get("/learn/progress").then((res) => setLearn(res.data)).catch(() => {});
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const lastJourney = journey?.last_journey;
  const JIcon = lastJourney ? ICONS[lastJourney.icon] || HandsPraying : HandsPraying;
  const totalSteps = lastJourney?.steps?.length || 6;
  const lastStepNum = lastJourney ? Math.min((lastJourney.current_step || 0) + 1, totalSteps) : 1;

  const hasActivity =
    (journey?.started ?? 0) > 0 ||
    (learn?.completed_lessons ?? 0) > 0 ||
    (data?.stats?.journal_entries ?? 0) > 0;

  const firstName = user?.name?.split(" ")[0] || "Friend";

  return (
    <div className="px-5 md:px-8 py-4 max-w-4xl mx-auto" data-testid="home-page">
      {/* Greeting */}
      <div className="mb-6" data-testid="home-greeting">
        <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium">
          {greeting}, {firstName}
        </h1>
        <p className="font-heading text-base sm:text-lg text-[#8B7355] italic mt-1">
          Let's take a moment together.
        </p>
      </div>

      {/* Daily Verse */}
      {data?.daily_verse && (
        <div className="glass-card-strong p-5 mb-6 relative overflow-hidden" data-testid="daily-verse-card">
          <div className="spiritual-orb-sm w-32 h-32 -top-8 -right-8 animate-orb opacity-70" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Star size={13} weight="fill" className="text-[#C4A265]" />
              <span className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium">
                Today's Verse
              </span>
            </div>
            <p className="font-heading text-base sm:text-lg text-[#2D1B0E] leading-relaxed mb-1.5 italic">
              "{data.daily_verse.text}"
            </p>
            <p className="text-[#C4A265] font-body text-xs font-medium">
              — {data.daily_verse.reference}
            </p>
            <p className="text-[#B8A088] font-body text-[11px] mt-2">
              Take this into your day.
            </p>
          </div>
        </div>
      )}

      {/* PRIMARY ACTION — visually dominant */}
      {hasActivity && lastJourney ? (
        <Link
          to={`/guided-prayer/${lastJourney.slug}`}
          onClick={() => track("home_continue_click", { slug: lastJourney.slug })}
          className="block mb-6 group"
          data-testid="continue-journey-card"
        >
          <div className="glass-card-strong p-6 sm:p-8 relative overflow-hidden transition-all group-hover:-translate-y-0.5">
            <div className="spiritual-orb-sm w-52 h-52 -top-12 -right-14 animate-orb opacity-70" />
            <div className="relative z-10 grid sm:grid-cols-[1fr_auto] gap-5 items-center">
              <div>
                <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-2 inline-flex items-center gap-1.5">
                  <Sparkle size={10} weight="fill" /> Continue Your Journey
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium leading-tight mb-2">
                  {lastJourney.completed ? "Walk it again." : "Pick up where you left off."}
                </h2>
                <p className="text-[#8B7355] font-body text-xs sm:text-sm mb-2 uppercase tracking-wider">
                  {lastJourney.completed
                    ? `${lastJourney.name} · just 3 minutes`
                    : `Day ${lastStepNum} of ${totalSteps} — just 3 minutes`}
                </p>
                <p className="text-[#5C4033] font-body text-sm sm:text-base mb-5 max-w-md leading-relaxed">
                  Find peace. Clear your mind. Hear God.
                </p>
                <span className="inline-flex items-center gap-2 bg-[#2D1B0E] group-hover:bg-[#3D2B1F] text-white rounded-full px-7 py-3.5 text-sm font-body font-medium transition-all shadow-md group-hover:shadow-lg" data-testid="continue-now-btn">
                  Continue Now <ArrowRight size={16} />
                </span>
              </div>
              <div className="hidden sm:flex items-center justify-center">
                <div className={`w-32 h-32 rounded-full ${lastJourney.color || "path-rose"} flex items-center justify-center shadow-inner`}>
                  <JIcon size={56} weight="duotone" className="text-[#5C4033]" />
                </div>
              </div>
            </div>
          </div>
        </Link>
      ) : (
        <Link
          to="/guided-prayer/peace"
          onClick={() => track("home_first_step_click", {})}
          className="block mb-6 group"
          data-testid="first-step-card"
        >
          <div className="glass-card-strong p-6 sm:p-8 relative overflow-hidden transition-all group-hover:-translate-y-0.5">
            <div className="spiritual-orb-sm w-52 h-52 -top-12 -right-14 animate-orb opacity-70" />
            <div className="relative z-10 grid sm:grid-cols-[1fr_auto] gap-5 items-center">
              <div>
                <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-2 inline-flex items-center gap-1.5">
                  <Sparkle size={10} weight="fill" /> New here? Start your first step
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium leading-tight mb-2">
                  Take your first guided breath.
                </h2>
                <p className="text-[#5C4033] font-body text-sm sm:text-base mb-5 max-w-md leading-relaxed">
                  6 simple guided moments to bring clarity and calm — anchored in Scripture, walked one breath at a time.
                </p>
                <span className="inline-flex items-center gap-2 bg-[#2D1B0E] group-hover:bg-[#3D2B1F] text-white rounded-full px-7 py-3.5 text-sm font-body font-medium transition-all shadow-md group-hover:shadow-lg" data-testid="start-journey-btn">
                  Start Your Journey <ArrowRight size={16} />
                </span>
              </div>
              <div className="hidden sm:flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#FAF5EC] via-[#EDE6DC] to-[#E5D9C7] flex items-center justify-center shadow-inner">
                  <HandsPraying size={56} weight="duotone" className="text-[#C4A265]" />
                </div>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* EMOTIONAL QUICK ENTRY — what do you need right now? */}
      <div className="mb-7" data-testid="emotional-entry">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-heading text-xl sm:text-2xl text-[#2D1B0E] font-medium">
            What's on your heart right now?
          </h2>
          <span className="text-[#B8A088] text-[11px] font-body italic hidden sm:inline">
            One tap. One breath.
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {EMOTIONAL_ENTRIES.map((e) => (
            <Link
              key={e.id}
              to={e.to}
              onClick={() => track("home_emotional_entry_click", { id: e.id })}
              data-testid={`emotion-${e.id}`}
              className="group glass-card hover:glass-card-strong px-4 py-5 transition-all hover:-translate-y-0.5"
            >
              <div className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2.5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FAF5EC] to-[#E5D9C7] flex items-center justify-center flex-shrink-0">
                  <e.icon size={18} weight="duotone" className="text-[#C4A265]" />
                </div>
                <p className="font-body text-sm font-medium text-[#2D1B0E] leading-snug">
                  {e.label}
                </p>
              </div>
            </Link>
          ))}
        </div>
        <Link
          to="/guided-prayer/new"
          onClick={() => track("home_personal_prayer_clicked", {})}
          data-testid="home-personal-cta"
          className="mt-3 block text-center text-[#C4A265] font-body text-xs font-medium hover:text-[#5C4033]"
        >
          Or tell Voice in your own words →
        </Link>
      </div>

      {/* ASK FOR GUIDANCE — Connect entry */}
      <Link
        to="/connect"
        onClick={() => track("home_guidance_click", {})}
        className="block mb-6 group"
        data-testid="guidance-card"
      >
        <div className="glass-card p-5 transition-all group-hover:-translate-y-0.5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl path-warm flex items-center justify-center flex-shrink-0">
            <ChatCircleDots size={22} weight="duotone" className="text-[#5C4033]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading text-base text-[#2D1B0E] font-medium">Ask for Guidance</p>
            <p className="font-body text-xs sm:text-sm text-[#8B7355] mt-0.5 leading-relaxed">
              Get Scripture-based answers to what you're facing.
            </p>
          </div>
          <span className="text-[#C4A265] font-body text-xs font-medium inline-flex items-center gap-1 flex-shrink-0">
            Ask a Question <ArrowRight size={12} />
          </span>
        </div>
      </Link>

      {/* COMMUNITY — You're not walking alone */}
      <Link
        to="/community"
        onClick={() => track("home_community_click", {})}
        className="block mb-7 group"
        data-testid="community-card"
      >
        <div className="glass-card-strong p-6 relative overflow-hidden transition-all group-hover:-translate-y-0.5">
          <div className="spiritual-orb-sm w-32 h-32 -bottom-10 -left-8 animate-orb opacity-50" />
          <div className="relative z-10 grid sm:grid-cols-[1fr_auto] gap-4 items-center">
            <div>
              <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-2 inline-flex items-center gap-1.5">
                <UsersThree size={11} weight="fill" /> Together
              </p>
              <h3 className="font-heading text-xl sm:text-2xl text-[#2D1B0E] font-medium mb-1.5">
                You're not walking alone.
              </h3>
              <p className="font-body text-sm text-[#5C4033] mb-3 leading-relaxed">
                Join others taking the same step today.
              </p>
              <ul className="text-[#8B7355] font-body text-xs space-y-1 mb-4">
                <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#C4A265]" /> Pray together</li>
                <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#C4A265]" /> Share encouragement</li>
                <li className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#C4A265]" /> Stay consistent</li>
              </ul>
              <span className="inline-flex items-center gap-1.5 text-[#5C4033] font-body text-sm font-medium" data-testid="walk-together-btn">
                Walk Together <ArrowRight size={14} />
              </span>
            </div>
            <div className="hidden sm:flex items-center -space-x-2">
              {["#E8D5B0", "#D5B898", "#C4A265", "#B89A7E"].map((c, i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white font-heading text-xs"
                  style={{ background: c }}
                >
                  {String.fromCharCode(74 + i)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Link>

      {/* Watch + Worship — clearly de-prioritized */}
      <p className="text-[#B8A088] text-[10px] uppercase tracking-[0.2em] font-body mb-2.5">
        Also for you
      </p>
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <Link to="/watch" data-testid="featured-watch" className="group">
          <div className="glass-card px-4 py-3 transition-all hover:-translate-y-0.5 flex items-center gap-3">
            <PlayCircle size={18} weight="duotone" className="text-[#C4A265] flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-body text-sm font-medium text-[#2D1B0E]">Watch</p>
              <p className="font-body text-[11px] text-[#8B7355] leading-snug truncate">
                Short teachings to anchor your week
              </p>
            </div>
          </div>
        </Link>
        <Link to="/worship" data-testid="featured-worship" className="group">
          <div className="glass-card px-4 py-3 transition-all hover:-translate-y-0.5 flex items-center gap-3">
            <MusicNotes size={18} weight="duotone" className="text-[#C4A265] flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-body text-sm font-medium text-[#2D1B0E]">Worship</p>
              <p className="font-body text-[11px] text-[#8B7355] leading-snug truncate">
                Take a moment to pause and worship
              </p>
            </div>
          </div>
        </Link>
      </div>

      {/* Faith Journey stats — only after engagement */}
      {hasActivity && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-6" data-testid="faith-stats">
          <div className="glass-card p-3 text-center" data-testid="stat-consistency">
            <p className="font-heading text-lg text-[#2D1B0E] font-medium">{journey?.consistency_pct ?? 0}%</p>
            <p className="font-body text-[10px] text-[#8B7355]">Prayer Consistency</p>
          </div>
          <div className="glass-card p-3 text-center" data-testid="stat-milestones">
            <p className="font-heading text-lg text-[#2D1B0E] font-medium">{journey?.completed ?? 0}</p>
            <p className="font-body text-[10px] text-[#8B7355]">Journey Milestones</p>
          </div>
          <div className="glass-card p-3 text-center" data-testid="stat-lessons">
            <p className="font-heading text-lg text-[#2D1B0E] font-medium">
              {learn?.completed_lessons ?? 0}
              <span className="text-[10px] text-[#8B7355] font-body">/{learn?.total_lessons ?? 0}</span>
            </p>
            <p className="font-body text-[10px] text-[#8B7355]">Lessons</p>
          </div>
          <div className="glass-card p-3 text-center" data-testid="stat-journal">
            <p className="font-heading text-lg text-[#2D1B0E] font-medium">{data?.stats?.journal_entries ?? 0}</p>
            <p className="font-body text-[10px] text-[#8B7355]">Journal</p>
          </div>
        </div>
      )}
    </div>
  );
}
